import type { CompanionMessageRow, CompanionOutboundRetryPayload } from "./CompanionRoomType";

const PREFIX = "companionRoomOutbox:";

export function buildCompanionOutboxStorageKey(
  userId: string,
  spaceId: string,
  chatRoomId: string
): string {
  return `${PREFIX}${userId.trim()}:${spaceId.trim()}:${chatRoomId.trim()}`;
}

export interface CompanionOutboxRecord {
  tempId: string;
  payload: CompanionOutboundRetryPayload;
  row: CompanionMessageRow;
}

function isOutboxRecord(v: unknown): v is CompanionOutboxRecord {
  if (v === null || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.tempId === "string" &&
    o.payload !== null &&
    typeof o.payload === "object" &&
    o.row !== null &&
    typeof o.row === "object" &&
    typeof (o.row as CompanionMessageRow).id === "string"
  );
}

export function readCompanionOutbox(storageKey: string): CompanionOutboxRecord[] {
  if (typeof localStorage === "undefined") return [];
  let raw: string | null;
  try {
    raw = localStorage.getItem(storageKey);
  } catch {
    return [];
  }
  if (raw == null || raw === "") return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isOutboxRecord);
  } catch {
    return [];
  }
}

export function writeCompanionOutbox(storageKey: string, records: CompanionOutboxRecord[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (records.length === 0) {
      localStorage.removeItem(storageKey);
    } else {
      localStorage.setItem(storageKey, JSON.stringify(records));
    }
  } catch {
    /* quota / private mode */
  }
}

export function persistCompanionOutboxFromState(
  storageKey: string,
  retryByMessageId: Map<string, CompanionOutboundRetryPayload>,
  chatMessages: CompanionMessageRow[]
): void {
  if (retryByMessageId.size === 0) {
    writeCompanionOutbox(storageKey, []);
    return;
  }
  const records: CompanionOutboxRecord[] = [];
  for (const [tempId, payload] of Array.from(retryByMessageId.entries())) {
    const row = chatMessages.find((m) => m.id === tempId);
    if (!row) continue;
    records.push({ tempId, payload, row: { ...row, outboundFailed: true } });
  }
  writeCompanionOutbox(storageKey, records);
}
